import { Map } from 'immutable';

import * as accountTypes from '../actions/accountSettingsActionTypes';
import * as t from '../actions/studyMainActionTypes';

const initialState = Map({
  backTo: 'app/study',
  contentByReference: undefined,
  contentByReferenceFetched: false,
  contentByReferenceFetching: false,
  contentByWarfareQuals: undefined,
  isLoadingQuestions: false,
  question: {},
  previousQuestion: {},

  counters: {
    Learning: 0,
    Review: 0,
    New: 0,
  },

  previousCounters: {
    Learning: 0,
    Review: 0,
    New: 0,
  },

  questionsData: [],
  contentStudyPlan: undefined,
  isChapter: false,
  isQual: false,

  countQuestions: 0,
  recaptchaTime: null,
  chapterId: null,
  pdfLink: { url: '', timestamp: null },
  isLoadingPdfLink: false,
  postExamChecklistPdfLink: { url: '', timestamp: null },
  isLoadingPostExamChecklistPdf: false,
  isLoadingReferences: false,
  loadResetChapter: false,
  breadcrumsQualsAndCerts: '',

  loadFavoriteQuestion: false,
  setFavoriteMessage: '',
  counterFavorite: {},
  counterMyFavorite: {},
  counterSubmitted: {},
  counterMySubmitted: {},
  submittedQuestions: [],

  isStudyFavoriteQuestion: false,
  isStudyingSubmittedQuestions: false,
  isStudyingMyFavoriteQuestions: false,
  isStudyingMySubmittedQuestions: false,
  resetChapterMessage: { type: '', text: '' },
  errorMessage: '',
  moreQuestions: false,

  audio: null,

  isLoadingSktData: false,
  questionSKTCount: null,
  chaptersSKTWithoutQuestions: null,
  chaptersSKTCount: null,

  // Format: { chapterId: { progress, accumulated, total } }, where progress
  // stores the id of the next question audio to reproduce
  audioPlaylistsProgressByChapter: {},
  audioPlaylistsProgressFetched: false,
  audioPlaylistsProgressFetching: false,
});

const reducer = function studyMainReducer(state = initialState, action) {
  switch (action.type) {
    case t.SET_CONTENT_BY_REFERENCE:
      return state
        .set('contentByReference', action.response)
        .set('contentByReferenceFetched', true)
        .set('contentByReferenceFetching', false);

    case t.GET_CONTENT_BY_REFERENCE:
      return state.set('contentByReferenceFetching', true);

    case t.SET_COUNT_QUESTIONS:
      return state.set('countQuestions', action.value);

    case t.ADD_COUNT_QUESTIONS: {
      const addCountQuestions = state.get('countQuestions') + 1;
      return state.set('countQuestions', addCountQuestions);
    }

    case t.SET_RECAPTCHA_TIME:
      return state.set('recaptchaTime', action.value);

    case t.SET_CHAPTER_ID:
      return state.set('chapterId', action.value);

    case t.SET_PDF_LINK:
      return state.set('pdfLink', {
        url: action.response,
        timestamp: Date.now(), // Force a new object to trigger componentDidUpdate
      });

    case t.SET_POST_EXAM_CHECKLIST_PDF_LINK:
      return state.set('postExamChecklistPdfLink', {
        url: action.response.pdfUrl,
        timestamp: Date.now(), // Force a new object to trigger componentDidUpdate
      });

    case t.SET_IS_LOADING_POST_EXAM_CHECKLIST_PDF:
      return state.set('isLoadingPostExamChecklistPdf', action.value);

    case t.SET_CONTENT_BY_WARFARE_QUALS:
      return state.set('contentByWarfareQuals', action.response);

    // Question actions
    case t.SET_QUESTIONS_BY_RATE:
      return state.set(
        'questionsData',
        state.get('questionsData').concat(action.response),
      );

    case t.SET_QUESTIONS_BY_QUALS:
      return state.set(
        'questionsData',
        state.get('questionsData').concat(action.response),
      );

    case t.SET_QUESTIONS_BY_CHAPTER:
      return state.set(
        'questionsData',
        state.get('questionsData').concat(action.response),
      );

    case t.UPDATE_COUNTERS: {
      if (!action.answer) {
        if (action.counterType !== null) {
          const counterType = action.counterType.toLowerCase();
          const counters = state.get('counters');
          counters[counterType] -= 1;
          return state.set('counters', counters);
        }
        return state;
      }

      const stackedQuestion = state.get('questionsData').slice();
      const counters = { ...state.get('counters') };
      counters[action.answer.counterType.toLowerCase()] =
        counters[action.answer.counterType.toLowerCase()] > 0
          ? counters[action.answer.counterType.toLowerCase()] - 1
          : counters[action.answer.counterType.toLowerCase()];
      if (
        action.answer.difficulty === 'Wrong' ||
        (!action.answer.isCorrect &&
          ((!action.answer.isFlashcard &&
            action.answer.difficulty === 'Normal') ||
            action.answer.difficulty === 'Hard')) ||
        (action.answer.difficulty === 'Hard' &&
          action.answer.correctAnswer &&
          action.answer.correctAnswer.hard &&
          action.answer.correctAnswer.hard.minutes)
      ) {
        counters.learning++;
      }
      return state
        .set('counters', counters)
        .set('questionsData', stackedQuestion);
    }

    case t.CLEAN_QUESTIONS:
      return state.set('questionsData', []).set('previousQuestion', {});

    case t.SET_QUESTION: {
      let questionsExceptActual = [];

      if (
        action &&
        action.questionsData &&
        Array.isArray(action.questionsData) &&
        action.questionsData.length > 0 &&
        action.question &&
        action.question.id
      ) {
        questionsExceptActual = action.questionsData.filter(
          (q) => q?.id !== action.question.id,
        );
        if (questionsExceptActual && questionsExceptActual.length === 0) {
          questionsExceptActual.push('The user has no questions to answer');
        }
      }
      return state
        .set('question', action.question)
        .set('questionsData', questionsExceptActual);
    }

    case t.SET_PREVIOUS_QUESTION:
      return state.set('previousQuestion', action.question);

    case t.SET_PREVIOUS_COUNTERS:
      return state.set('previousCounters', action.counters);

    case t.SET_BACKED_STATE_TO_QUESTION: {
      const previousQuestion = { ...state.get('previousQuestion') };
      previousQuestion.backed = true;
      return state.set('previousQuestion', previousQuestion);
    }

    case t.PREVIOUS_QUESTION_TO_ACTUAL:
      return state.set('question', state.get('previousQuestion'));

    case t.PREVIOUS_COUNTERS_TO_ACTUAL:
      return state.set('counters', state.get('previousCounters'));

    case t.SET_COUNTERS:
      return state.set('counters', action.counters);

    case t.SET_MORE_QUESTIONS:
      return state.set('moreQuestions', action.value);

    case t.SET_IS_LOADING_QUESTIONS:
      return state.set('isLoadingQuestions', action.isLoadingQuestions);

    case t.MESSAGE_ERROR_GET_QUESTIONS:
      return state.set('errorMessage', action.errorMessage);

    case t.SET_IS_LOADING_PDF_LINK:
      return state.set('isLoadingPdfLink', action.value);

    case t.SET_IS_LOADING_REFERENCES:
      return state.set('isLoadingReferences', action.value);

    case t.SET_IS_LOADING_SKT_DATA:
      return state.set('isLoadingSktData', action.value);

    case t.SET_BACK_TO:
      return state.set('backTo', action.backTo);

    case t.SET_ANSWER_RESPONSE:
      return state.set('answerResponse', action.response);
    case t.BREADCRUMS_QUALS_AND_CERTS:
      return state.set('breadcrumsQualsAndCerts', action.breadcrums);

    case t.LOAD_FAVORITE_QUESTION:
      return state.set('loadFavoriteQuestion', action.status);

    case t.SET_FAVORITE_MESSAGE:
      return state.set('setFavoriteMessage', action.status);

    case t.SET_IS_STUDY_FAVORITE_QUESTION:
      return state.set('isStudyFavoriteQuestion', action.status);

    case t.SET_COUNTER_FAVORITE:
      return state.set('counterFavorite', action.response);

    case t.SET_COUNTER_MY_FAVORITE:
      return state.set('counterMyFavorite', action.response);

    case t.SET_COUNTER_MY_SUBMITTED:
      return state.set('counterMySubmitted', action.response);

    case t.SET_SUBMITTED_QUESTIONS:
      return state
        .set(
          // used for studying
          'questionsData',
          state.get('questionsData').concat(action.questions),
        ) // used for listing
        .set('submittedQuestions', action.questions);
    case t.SET_FAVORITE_QUESTIONS_FOR_STUDY:
      return state.set(
        // used for studying
        'questionsData',
        state.get('questionsData').concat(action.questions),
      );
    case t.SET_COUNTER_SUBMITTED:
      return state.set('counterSubmitted', action.counters);

    case t.SET_IS_STUDYING_SUBMITTED_QUESTIONS:
      return state.set('isStudyingSubmittedQuestions', action.status);

    case t.SET_IS_STUDYING_MY_FAVORITE_QUESTIONS:
      return state.set('isStudyingMyFavoriteQuestions', action.status);

    case t.SET_IS_STUDYING_MY_SUBMITTED_QUESTIONS:
      return state.set('isStudyingMySubmittedQuestions', action.status);

    case t.SET_DISABLED_CHAPTER:
      return state.set('disabledChapters', action.response);

    case t.SET_AUDIO_PLAYLIST:
      return state.set('audioPlaylist', action.audio);

    case t.ADD_AUDIO_PLAYLISTS_PROGRESS: {
      const savedProgressByChapter = {
        ...state.get('audioPlaylistsProgressByChapter'),
      };
      const newProgressByChapter = action.payload.progressByChapter;

      newProgressByChapter.forEach((newChapterProgress) => {
        savedProgressByChapter[newChapterProgress.chapterId] = {
          nextQuestionId: newChapterProgress.nextQuestionId,
          status: newChapterProgress.status,
          accumulated: newChapterProgress.accumulated,
          total: newChapterProgress.total,
        };
      });

      return state
        .set('audioPlaylistsProgressByChapter', savedProgressByChapter)
        .set('audioPlaylistsProgressFetched', true)
        .set('audioPlaylistsProgressFetching', false);
    }
    case t.GET_AUDIO_PLAYLISTS_PROGRESS:
      return state.set('audioPlaylistsProgressFetching', true);
    case t.UPDATE_AUDIO_PLAYLIST_PROGRESS: {
      const savedProgressByChapter = {
        ...state.get('audioPlaylistsProgressByChapter'),
      };

      const chapterId = action.payload.chapterId;
      const chapterSavedProgress = savedProgressByChapter[chapterId];
      savedProgressByChapter[chapterId] = {
        ...chapterSavedProgress,
        nextQuestionId: action.payload.nextQuestionId,
        status: action.payload.status,
        accumulated: action.payload.restartPlaylist
          ? 0
          : Math.min(
              chapterSavedProgress.total,
              chapterSavedProgress.accumulated + 1,
            ),
      };

      return state.set(
        'audioPlaylistsProgressByChapter',
        savedProgressByChapter,
      );
    }
    case accountTypes.SWITCH_PFE_SKT:
      return state
        .set('audioPlaylistsProgressByChapter', {})
        .set('audioPlaylistsProgressFetched', false)
        .set('audioPlaylistsProgressFetching', false)
        .set('contentByReferenceFetched', false)
        .set('contentByReferenceFetching', false);
    case t.SET_RESET_CHAPTER_MESSAGE:
      return state.set('resetChapterMessage', action.message);
    case t.LOAD_RESET_CHAPTER:
      return state.set('loadResetChapter', action.value);
    case t.SET_RESET_CHAPTER:
      return state.set('getResetChapter', action.value);
    case t.SET_SKT_COUNTERS:
      return state
        .set('questionSKTCount', action.value.questionSKTCount)
        .set(
          'chaptersSKTWithoutQuestions',
          action.value.chaptersSKTWithoutQuestions,
        )
        .set('chaptersSKTCount', action.value.chaptersSKTCount);
    default:
      return state;
  }
};

export default reducer;
